import React, { useState } from 'react';
import { useCRM } from '../../context/CRMContext';
import SafeIcon from '../../common/SafeIcon';
import { mockAI } from '../../services/mockAI';

const SocialPostForm = ({ post, onClose, connectedChannels = [] }) => {
  const { addSocialPost, updateSocialPost, users, projects } = useCRM();

  const [formData, setFormData] = useState(post || {
    user_id: '',
    project_id: '',
    account_ids: [],
    content: '',
    hashtags: '',
    scheduled_for: '',
    status: 'Kladd',
    image_url: ''
  });

  const [aiLoading, setAiLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [activeImageTab, setActiveImageTab] = useState('url');

  const toggleAccount = (accountId) => {
    setFormData(prev => {
      const isSelected = prev.account_ids.includes(accountId);
      if (isSelected) {
        return { ...prev, account_ids: prev.account_ids.filter(id => id !== accountId) };
      } else {
        return { ...prev, account_ids: [...prev.account_ids, accountId] };
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.account_ids.length === 0) {
      alert("Du må velge minst én konto å poste til.");
      return;
    }

    const processedData = {
      ...formData,
      hashtags: typeof formData.hashtags === 'string' ? formData.hashtags.split(',').map(h => h.trim()) : formData.hashtags
    };

    if (!post) {
      formData.account_ids.forEach(accId => {
        const acc = connectedChannels.find(a => a.id === accId);
        if (addSocialPost) {
          addSocialPost({ ...processedData, account_id: accId, channel: acc ? acc.name : 'Unknown' });
        }
      });
    } else {
      const acc = connectedChannels.find(a => a.id === formData.account_ids[0]);
      if (updateSocialPost) {
        updateSocialPost(post.post_id, { ...processedData, account_id: formData.account_ids[0], channel: acc ? acc.name : 'Unknown' });
      }
    }
    onClose();
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleGeneratePost = async () => {
    if (!formData.project_id) {
      alert("Vennligst velg et prosjekt først for at AI skal skjønne konteksten.");
      return;
    }
    const project = projects.find(p => p.project_id === formData.project_id || p.id === formData.project_id);
    if (!project) return;

    setAiLoading(true);
    try {
      const generatedContent = await mockAI.generateSocialPost(project, 'Multiple');
      setFormData(prev => ({ ...prev, content: generatedContent }));
    } catch (e) {
      console.error(e);
    } finally {
      setAiLoading(false);
    }
  };

  const handleGenerateImage = () => {
    setImageLoading(true);
    setTimeout(() => {
      const randomId = Math.floor(Math.random() * 1000);
      setFormData(prev => ({
        ...prev,
        image_url: `https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&q=80&auto=format&fit=crop&random=${randomId}`
      }));
      setImageLoading(false);
    }, 1500);
  };

  const getIcon = (iconName) => {
    switch (iconName) {
      case 'linkedin': return '👔';
      case 'facebook': return '📘';
      case 'instagram': return '📸';
      case 'x': return '𝕏';
      case 'tiktok': return '🎵';
      case 'google': return '🌐';
      default: return '🔗';
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Top Controls */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Publiser til (Velg flere)</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {connectedChannels.map(acc => {
              const isSelected = formData.account_ids.includes(acc.id);
              return (
                <label key={acc.id} className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${isSelected ? 'border-primary-500 bg-primary-50' : 'border-slate-100 hover:border-slate-300'}`}>
                  <input type="checkbox" className="hidden" checked={isSelected} onChange={() => toggleAccount(acc.id)} />
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isSelected ? 'bg-primary-600 text-white' : 'bg-slate-200 text-slate-500'}`}>
                    <SafeIcon icon={getIcon(acc.icon)} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-slate-900 truncate">{acc.name}</p>
                  </div>
                  {isSelected && <SafeIcon icon="✓" className="text-primary-600" />}
                </label>
              )
            })}
            {connectedChannels.length === 0 && (
              <div className="col-span-full text-xs text-slate-500 italic p-2">Ingen kanaler tilkoblet. Sjekk integrasjoner.</div>
            )}
          </div>
        </div>

        <div>
          <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1">Ansvarlig Bruker</label>
          <select name="user_id" value={formData.user_id} onChange={handleChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 outline-none font-bold text-sm">
            <option value="">-- Velg bruker --</option>
            {(users || []).map(u => (
              <option key={u.user_id} value={u.user_id}>{u.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1">Knyttet prosjekt</label>
          <select name="project_id" value={formData.project_id} onChange={handleChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 outline-none font-bold text-sm">
            <option value="">Ingen</option>
            {(projects || []).map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Content Generator */}
      <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
        <div className="flex justify-between items-center mb-3">
          <label className="block text-xs font-black text-slate-500 uppercase tracking-widest">Tekstinnhold</label>
          <button type="button" onClick={handleGeneratePost} disabled={aiLoading} className="px-3 py-1.5 bg-white border border-primary-200 text-primary-600 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-primary-50 transition-all flex items-center gap-2 shadow-sm">
            {aiLoading ? <SafeIcon icon="🔄" className="animate-spin w-3 h-3" /> : <SafeIcon icon="✨" className="w-3 h-3" />}
            Generer med AI
          </button>
        </div>
        <textarea
          required
          name="content"
          rows="5"
          value={formData.content}
          onChange={handleChange}
          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:border-primary-500 outline-none font-medium text-sm leading-relaxed resize-none"
          placeholder="Skriv innlegget her..."
        ></textarea>
        <div className="mt-3">
          <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Hashtags</label>
          <input
            name="hashtags"
            value={Array.isArray(formData.hashtags) ? formData.hashtags.join(', ') : formData.hashtags}
            onChange={handleChange}
            className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl focus:border-primary-500 outline-none text-xs font-medium text-blue-600"
            placeholder="#salg, #crm"
          />
        </div>
      </div>

      {/* Image Generator Section */}
      <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
        <div className="flex justify-between items-center mb-4">
          <label className="block text-xs font-black text-slate-500 uppercase tracking-widest">Media / Bilde</label>
          <div className="flex bg-white p-1 rounded-lg border border-slate-200">
            <button type="button" onClick={() => setActiveImageTab('url')} className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${activeImageTab === 'url' ? 'bg-slate-100 text-slate-900' : 'text-slate-400 hover:text-slate-600'}`}>
              Link
            </button>
            <button type="button" onClick={() => setActiveImageTab('ai')} className={`px-3 py-1 text-[10px] font-bold rounded-md transition-all ${activeImageTab === 'ai' ? 'bg-primary-50 text-primary-700' : 'text-slate-400 hover:text-slate-600'}`}>
              AI Bilde
            </button>
          </div>
        </div>

        {activeImageTab === 'url' && (
          <input
            name="image_url"
            value={formData.image_url}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:border-primary-500 outline-none text-sm"
            placeholder="https://example.com/image.jpg"
          />
        )}

        {activeImageTab === 'ai' && (
          <div className="flex gap-4">
            <button type="button" onClick={handleGenerateImage} disabled={imageLoading} className="flex-1 py-3 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:shadow-lg transition-all flex items-center justify-center gap-2">
              {imageLoading ? <SafeIcon icon="🔄" className="animate-spin w-4 h-4" /> : <SafeIcon icon="🎨" className="w-4 h-4" />}
              Generer AI Bilde
            </button>
          </div>
        )}

        {/* Image Preview */}
        {formData.image_url && (
          <div className="mt-4 relative group rounded-xl overflow-hidden border border-slate-200 shadow-sm">
            <img src={formData.image_url} alt="Preview" className="w-full h-48 object-cover" />
            <button type="button" onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))} className="absolute top-2 right-2 p-2 bg-white/90 text-red-500 rounded-lg hover:bg-white transition-all shadow-sm opacity-0 group-hover:opacity-100">
              <SafeIcon icon="🗑️" />
            </button>
          </div>
        )}
      </div>

      {/* Scheduling & Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1">Planlagt publisering</label>
          <input type="datetime-local" name="scheduled_for" value={formData.scheduled_for} onChange={handleChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 outline-none font-bold text-sm text-slate-700" />
        </div>
        <div>
          <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-1">Status</label>
          <select name="status" value={formData.status} onChange={handleChange} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:border-primary-500 outline-none font-bold text-sm">
            <option value="Kladd">Kladd</option>
            <option value="Planlagt">Planlagt</option>
            <option value="Publisert">Publisert</option>
          </select>
        </div>
      </div>

      <div className="flex justify-end pt-4 border-t border-slate-100">
        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="px-6 py-3 text-slate-500 font-bold text-sm hover:bg-slate-50 rounded-xl transition-all">
            Avbryt
          </button>
          <button type="submit" className="px-8 py-3 bg-slate-900 text-white rounded-xl font-bold text-sm hover:bg-slate-800 transition-all shadow-lg">
            <SafeIcon icon="📤" className="w-4 h-4 mr-2 inline" /> Publiser / Lagre ({formData.account_ids.length} kontoer)
          </button>
        </div>
      </div>
    </form>
  );
};

export default SocialPostForm;